import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { DocumentItem, MultiDocumentInfo } from '../../../services/documentApi';
import './DocumentAiPdfViewer.scss';

interface DocumentAiPdfViewerProps {
    document: DocumentItem | null;
    title?: string;
    hoveredEntityType?: string | null;
    focusedEntityType?: string | null;
    onClearFocus?: () => void;
    /** Child documents if this is a parent with split documents */
    childDocuments?: DocumentItem[];
    /** Callback when user clicks on a child document tab */
    onSelectChildDocument?: (childId: string) => void;
}

// Backend entity structure
interface EntityWithBounds {
    type: string;
    value: string;
    normalized_value?: any;
    confidence: number;
    bounding_box?: Array<{ x: number; y: number }>; // 4 vertices, normalized 0-1
    page?: number; // 0-indexed page number
}

const PDF_SCALE = 0.9;

// Color mapping for different entity types
const getEntityColor = (type: string): string => {
    const colors: Record<string, string> = {
        // Amounts (green)
        'total_amount': 'rgba(34, 197, 94, 0.25)',
        'net_amount': 'rgba(34, 197, 94, 0.2)',
        'total_tax_amount': 'rgba(251, 191, 36, 0.25)',
        // Supplier info (blue)
        'supplier_name': 'rgba(59, 130, 246, 0.25)',
        'supplier_address': 'rgba(59, 130, 246, 0.15)',
        'supplier_email': 'rgba(59, 130, 246, 0.2)',
        'supplier_phone': 'rgba(59, 130, 246, 0.2)',
        'supplier_website': 'rgba(59, 130, 246, 0.2)',
        'supplier_iban': 'rgba(59, 130, 246, 0.2)',
        'supplier_tax_id': 'rgba(59, 130, 246, 0.2)',
        // Receiver info (cyan)
        'receiver_name': 'rgba(14, 165, 233, 0.25)',
        'receiver_address': 'rgba(14, 165, 233, 0.15)',
        // Invoice metadata (purple/pink)
        'invoice_id': 'rgba(139, 92, 246, 0.25)',
        'invoice_date': 'rgba(236, 72, 153, 0.25)',
        'invoice_type': 'rgba(139, 92, 246, 0.2)',
        'due_date': 'rgba(236, 72, 153, 0.2)',
        'currency': 'rgba(234, 179, 8, 0.2)',
        // Line items (indigo)
        'line_item': 'rgba(99, 102, 241, 0.15)',
        // Line item properties
        'line_item/description': 'rgba(249, 115, 22, 0.2)',
        'line_item/quantity': 'rgba(168, 85, 247, 0.2)',
        'line_item/unit_price': 'rgba(34, 197, 94, 0.2)',
        'line_item/amount': 'rgba(34, 197, 94, 0.25)',
        'line_item/product_code': 'rgba(99, 102, 241, 0.25)',
        'line_item/unit': 'rgba(168, 85, 247, 0.15)',
    };
    // Check for partial matches (line_item/ prefix)
    if (type.startsWith('line_item/')) {
        for (const [key, value] of Object.entries(colors)) {
            if (key.startsWith('line_item/') && type.includes(key.split('/')[1])) return value;
        }
        return 'rgba(99, 102, 241, 0.15)';
    }
    return colors[type] || 'rgba(156, 163, 175, 0.2)';
};

const getBorderColor = (type: string): string => {
    const colors: Record<string, string> = {
        // Amounts (green)
        'total_amount': 'rgb(34, 197, 94)',
        'net_amount': 'rgb(34, 197, 94)',
        'total_tax_amount': 'rgb(251, 191, 36)',
        // Supplier info (blue)
        'supplier_name': 'rgb(59, 130, 246)',
        'supplier_address': 'rgb(59, 130, 246)',
        'supplier_email': 'rgb(59, 130, 246)',
        'supplier_phone': 'rgb(59, 130, 246)',
        'supplier_website': 'rgb(59, 130, 246)',
        'supplier_iban': 'rgb(59, 130, 246)',
        'supplier_tax_id': 'rgb(59, 130, 246)',
        // Receiver info (cyan)
        'receiver_name': 'rgb(14, 165, 233)',
        'receiver_address': 'rgb(14, 165, 233)',
        // Invoice metadata (purple/pink)
        'invoice_id': 'rgb(139, 92, 246)',
        'invoice_date': 'rgb(236, 72, 153)',
        'invoice_type': 'rgb(139, 92, 246)',
        'due_date': 'rgb(236, 72, 153)',
        'currency': 'rgb(234, 179, 8)',
        // Line items (indigo)
        'line_item': 'rgb(99, 102, 241)',
        // Line item properties
        'line_item/description': 'rgb(249, 115, 22)',
        'line_item/quantity': 'rgb(168, 85, 247)',
        'line_item/unit_price': 'rgb(34, 197, 94)',
        'line_item/amount': 'rgb(34, 197, 94)',
        'line_item/product_code': 'rgb(99, 102, 241)',
        'line_item/unit': 'rgb(168, 85, 247)',
    };
    if (type.startsWith('line_item/')) {
        for (const [key, value] of Object.entries(colors)) {
            if (key.startsWith('line_item/') && type.includes(key.split('/')[1])) return value;
        }
        return 'rgb(99, 102, 241)';
    }
    return colors[type] || 'rgb(156, 163, 175)';
};

const DocumentAiPdfViewer: React.FC<DocumentAiPdfViewerProps> = ({
    document,
    title,
    hoveredEntityType,
    focusedEntityType,
    onClearFocus,
    childDocuments,
    onSelectChildDocument
}) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [canvasRect, setCanvasRect] = useState<{ width: number; height: number; left: number; top: number } | null>(null);
    const pageWrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Multi-document info
    const multiDoc = document?.multi_document;
    const isMultiDocument = multiDoc?.detected && (multiDoc?.document_count ?? 1) > 1;
    const isParent = multiDoc?.is_parent;
    const isChild = multiDoc?.is_child || !!document?.parent_document_id;

    // Get the starting page for this document from boundary info
    const getDocumentStartPage = useCallback((): number | null => {
        // Check boundaries for page info
        const boundaries = multiDoc?.boundaries;
        if (boundaries && boundaries.length > 0) {
            const firstBoundary = boundaries[0];
            // Use pages array if available, otherwise use page field
            if (firstBoundary.pages && firstBoundary.pages.length > 0) {
                return firstBoundary.pages[0];
            }
            if (typeof firstBoundary.page === 'number') {
                return firstBoundary.page;
            }
        }
        return null;
    }, [multiDoc]);

    // Auto-navigate to document's page when document changes (especially for child docs)
    useEffect(() => {
        // Debug: Log all relevant data
        console.log('[PDF Viewer] Document changed:', {
            id: document?.id,
            filename: document?.filename,
            isChild,
            parent_document_id: document?.parent_document_id,
            split_index: document?.split_index,
            multi_document: document?.multi_document,
            boundaries: document?.multi_document?.boundaries
        });

        const startPage = getDocumentStartPage();
        console.log('[PDF Viewer] Calculated startPage:', startPage);

        if (startPage !== null && startPage >= 0) {
            // pageNumber is 1-indexed, page field is 0-indexed
            const targetPage = startPage + 1;
            console.log(`[PDF Viewer] Auto-navigating to page ${targetPage} for document`);
            setPageNumber(targetPage);
            setCanvasRect(null); // Reset canvas for new page
        } else {
            // Reset to page 1 for new documents without boundary info
            console.log('[PDF Viewer] No boundary info, staying at page 1');
            setPageNumber(1);
        }
    }, [document?.id, getDocumentStartPage, isChild]);

    // Zoom when focused
    const isZoomed = !!focusedEntityType;
    const currentScale = isZoomed ? 1.5 : PDF_SCALE;

    // Get canvas size and position after render
    useEffect(() => {
        if (pageWrapperRef.current) {
            const canvas = pageWrapperRef.current.querySelector('canvas');
            if (canvas) {
                // Get canvas position relative to the wrapper
                const wrapperRect = pageWrapperRef.current.getBoundingClientRect();
                const canvasRectVal = canvas.getBoundingClientRect();
                setCanvasRect({
                    width: canvas.offsetWidth,
                    height: canvas.offsetHeight,
                    left: canvasRectVal.left - wrapperRect.left,
                    top: canvasRectVal.top - wrapperRect.top
                });
            }
        }
    }, [pageNumber, loading, currentScale]);

    // Scroll to focused entity
    useEffect(() => {
        if (focusedEntityType && canvasRect && contentRef.current) {
            const entities = document?.extracted_data?.entities_with_bounds || [];
            const targetEntity = entities.find((e: EntityWithBounds) => e.type === focusedEntityType);

            if (targetEntity?.bounding_box && targetEntity.bounding_box.length >= 4) {
                const vertices = targetEntity.bounding_box;
                const ys = vertices.map((v: { x: number; y: number }) => v.y || 0);
                const minY = Math.min(...ys);

                // Calculate scroll position
                const scrollTop = minY * canvasRect.height - 100;
                contentRef.current.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
            }
        }
    }, [focusedEntityType, canvasRect, document]);

    const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(null);
    }, []);

    const handleLoadError = useCallback((err: Error) => {
        console.error('PDF load error:', err);
        setError('Failed to load PDF');
        setLoading(false);
    }, []);

    const handleRenderSuccess = useCallback(() => {
        // Get actual canvas dimensions and position after render
        setTimeout(() => {
            if (pageWrapperRef.current) {
                const canvas = pageWrapperRef.current.querySelector('canvas');
                if (canvas) {
                    const wrapperRect = pageWrapperRef.current.getBoundingClientRect();
                    const canvasRectVal = canvas.getBoundingClientRect();
                    setCanvasRect({
                        width: canvas.offsetWidth,
                        height: canvas.offsetHeight,
                        left: canvasRectVal.left - wrapperRect.left,
                        top: canvasRectVal.top - wrapperRect.top
                    });
                }
            }
        }, 50);
    }, []);

    const goToPrevPage = useCallback(() => {
        setPageNumber(prev => Math.max(1, prev - 1));
        setCanvasRect(null);
    }, []);

    const goToNextPage = useCallback(() => {
        setPageNumber(prev => Math.min(numPages || prev, prev + 1));
        setCanvasRect(null);
    }, [numPages]);

    // Get entities with bounding boxes for CURRENT PAGE ONLY
    const getEntities = useCallback((): EntityWithBounds[] => {
        const entities = document?.extracted_data?.entities_with_bounds || [];

        // Filter by: has bounding box AND matches current page (0-indexed)
        const currentPageIndex = pageNumber - 1; // pageNumber is 1-indexed, page field is 0-indexed
        const result = entities.filter((entity: EntityWithBounds) => {
            const hasBbox = entity.bounding_box && entity.bounding_box.length >= 4;
            // If entity has no page info, assume page 0 (single page docs)
            const entityPage = entity.page ?? 0;
            const matchesPage = entityPage === currentPageIndex;
            return hasBbox && matchesPage;
        });

        // Debug: log what we're showing
        console.log(`Page ${pageNumber}: showing ${result.length} entities (${entities.length} total)`);

        return result;
    }, [document, pageNumber]);

    // Convert vertices array to CSS position
    const getBboxStyle = useCallback((entity: EntityWithBounds, isHovered: boolean): React.CSSProperties | null => {
        if (!entity.bounding_box || entity.bounding_box.length < 4 || !canvasRect) {
            return null;
        }

        const vertices = entity.bounding_box;

        // Get min/max from 4 vertices (normalized 0-1)
        const xs = vertices.map(v => v.x || 0);
        const ys = vertices.map(v => v.y || 0);

        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);

        // Convert normalized coords to pixels using actual canvas size
        const left = minX * canvasRect.width;
        const top = minY * canvasRect.height;
        const width = (maxX - minX) * canvasRect.width;
        const height = (maxY - minY) * canvasRect.height;

        // Skip very small boxes
        if (width < 5 || height < 5) return null;

        // Hovered entity gets stronger highlight
        if (isHovered) {
            return {
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                height: `${height}px`,
                backgroundColor: 'rgba(255, 235, 59, 0.5)',
                pointerEvents: 'none',
                boxSizing: 'border-box',
                zIndex: 10,
                transition: 'all 0.15s ease',
            };
        }

        return {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: getEntityColor(entity.type),
            pointerEvents: 'none',
            boxSizing: 'border-box',
            transition: 'all 0.15s ease',
        };
    }, [canvasRect]);

    if (!document) {
        return (
            <div className="docai-pdf-viewer docai-pdf-viewer--empty">
                {title && <div className="docai-pdf-viewer__header">{title}</div>}
                <div className="docai-pdf-viewer__placeholder">
                    <span>No document available</span>
                </div>
            </div>
        );
    }

    const fileUrl = `http://localhost:5003/api/documents/${document.id}/download?token=${localStorage.getItem('auth_token')}`;
    const entities = getEntities();

    // Render multi-document warning banner
    const renderMultiDocBanner = () => {
        if (!isMultiDocument) return null;

        const docCount = multiDoc?.document_count ?? 2;
        const confidence = multiDoc?.confidence ?? 0.8;
        const confidencePercent = Math.round(confidence * 100);

        // Different colors based on confidence
        const isHighConfidence = confidence >= 0.8;
        const bannerClass = isHighConfidence
            ? 'docai-pdf-viewer__multi-doc-banner--warning'
            : 'docai-pdf-viewer__multi-doc-banner--info';

        return (
            <div className={`docai-pdf-viewer__multi-doc-banner ${bannerClass}`}>
                <div className="docai-pdf-viewer__multi-doc-banner-content">
                    <span className="docai-pdf-viewer__multi-doc-icon">
                        {isHighConfidence ? '⚠️' : 'ℹ️'}
                    </span>
                    <span className="docai-pdf-viewer__multi-doc-text">
                        <strong>{docCount} farklı belge tespit edildi</strong>
                        {isParent && childDocuments && childDocuments.length > 0 && (
                            <span> - Her biri ayrı kaydedildi</span>
                        )}
                    </span>
                    <span className="docai-pdf-viewer__multi-doc-confidence">
                        {confidencePercent}% güven
                    </span>
                </div>
                {isParent && childDocuments && childDocuments.length > 0 && (
                    <div className="docai-pdf-viewer__multi-doc-tabs">
                        <button
                            className="docai-pdf-viewer__multi-doc-tab docai-pdf-viewer__multi-doc-tab--active"
                            title="Ana belge (Sayfa 1)"
                        >
                            Belge 1
                        </button>
                        {childDocuments.map((child, idx) => {
                            // Get page info from child's boundary
                            const childBoundary = child.multi_document?.boundaries?.[0];
                            let pageHint = '';
                            if (childBoundary) {
                                const pages = childBoundary.pages;
                                if (pages && pages.length > 0) {
                                    pageHint = pages.length === 1
                                        ? ` (S.${pages[0] + 1})`
                                        : ` (S.${pages[0] + 1}-${pages[pages.length - 1] + 1})`;
                                } else if (typeof childBoundary.page === 'number') {
                                    pageHint = ` (S.${childBoundary.page + 1})`;
                                }
                            }

                            return (
                                <button
                                    key={child.id}
                                    className="docai-pdf-viewer__multi-doc-tab"
                                    onClick={() => onSelectChildDocument?.(child.id)}
                                    title={`${child.extracted_data?.supplier_name || `Belge ${idx + 2}`}${pageHint}`}
                                >
                                    Belge {idx + 2}{pageHint}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // Render child document indicator
    const renderChildIndicator = () => {
        if (!isChild) return null;

        const splitIndex = (document?.split_index ?? 0) + 1;

        // Get page range info from boundaries
        const boundaries = multiDoc?.boundaries;
        let pageInfo = '';
        if (boundaries && boundaries.length > 0) {
            const firstBoundary = boundaries[0];
            const pages = firstBoundary.pages;
            if (pages && pages.length > 0) {
                if (pages.length === 1) {
                    pageInfo = ` (Sayfa ${pages[0] + 1})`;
                } else {
                    const sortedPages = [...pages].sort((a, b) => a - b);
                    pageInfo = ` (Sayfa ${sortedPages[0] + 1}-${sortedPages[sortedPages.length - 1] + 1})`;
                }
            } else if (typeof firstBoundary.page === 'number') {
                pageInfo = ` (Sayfa ${firstBoundary.page + 1})`;
            }
        }

        return (
            <div className="docai-pdf-viewer__child-indicator">
                <span>📄 Alt Belge #{splitIndex}{pageInfo}</span>
            </div>
        );
    };

    return (
        <div className="docai-pdf-viewer">
            {title && <div className="docai-pdf-viewer__header">{title}</div>}
            {renderMultiDocBanner()}
            {renderChildIndicator()}

            <div
                className="docai-pdf-viewer__content"
                ref={contentRef}
                onClick={() => isZoomed && onClearFocus?.()}
                style={{ cursor: isZoomed ? 'zoom-out' : 'default' }}
            >
                <div className="docai-pdf-viewer__page-wrapper" ref={pageWrapperRef}>
                    <Document
                        file={fileUrl}
                        onLoadSuccess={handleLoadSuccess}
                        onLoadError={handleLoadError}
                        loading={<div className="docai-pdf-viewer__loading">Loading PDF...</div>}
                    >
                        {!error && (
                            <Page
                                pageNumber={pageNumber}
                                scale={currentScale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onRenderSuccess={handleRenderSuccess}
                            />
                        )}
                    </Document>

                    {/* Bounding Box Overlays */}
                    {canvasRect && entities.length > 0 && (
                        <div
                            className="docai-pdf-viewer__overlay"
                            style={{
                                width: canvasRect.width,
                                height: canvasRect.height,
                                left: canvasRect.left,
                                top: canvasRect.top,
                            }}
                        >
                            {entities.map((entity, idx) => {
                                const isHovered = hoveredEntityType === entity.type ||
                                    (hoveredEntityType && entity.type.startsWith(hoveredEntityType + '/'));
                                const style = getBboxStyle(entity, !!isHovered);
                                if (!style) return null;
                                return (
                                    <div
                                        key={`entity-${idx}-${entity.type}`}
                                        style={style}
                                        title={`${entity.type}: ${entity.value}`}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {error && (
                    <div className="docai-pdf-viewer__error">
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {numPages && numPages > 1 && (
                <div className="docai-pdf-viewer__controls">
                    <button onClick={goToPrevPage} disabled={pageNumber <= 1}>
                        &#x2039;
                    </button>
                    <span>{pageNumber} / {numPages}</span>
                    <button onClick={goToNextPage} disabled={pageNumber >= numPages}>
                        &#x203a;
                    </button>
                </div>
            )}

            {/* Legend */}
            {entities.length > 0 && (
                <div className="docai-pdf-viewer__legend">
                    <span className="docai-pdf-viewer__legend-title">Document AI:</span>
                    <span style={{ background: 'rgba(34, 197, 94, 0.3)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', border: '1px solid rgb(34, 197, 94)' }}>Total</span>
                    <span style={{ background: 'rgba(59, 130, 246, 0.3)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', border: '1px solid rgb(59, 130, 246)' }}>Supplier</span>
                    <span style={{ background: 'rgba(139, 92, 246, 0.3)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', border: '1px solid rgb(139, 92, 246)' }}>Invoice #</span>
                    <span style={{ background: 'rgba(99, 102, 241, 0.2)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', border: '1px solid rgb(99, 102, 241)' }}>Items</span>
                </div>
            )}

            {entities.length === 0 && !loading && (
                <div style={{ padding: '8px 12px', fontSize: '11px', color: '#94a3b8', background: '#f1f5f9' }}>
                    No bounding box data from Document AI
                </div>
            )}
        </div>
    );
};

export default DocumentAiPdfViewer;
